/**
 * Runtime configuration, read from the `/config.js` the container generates.
 *
 * Deliberately not `import.meta.env`: Vite inlines those at build time, which
 * would mean rebuilding the image per environment. See index.html.
 */

export interface OidcConfig {
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scope: string;
  /**
   * Optional. Sent as the `audience` authorize parameter. Ory Hydra only puts
   * `aud` in the access token when the request asks for it; the mock adds it on
   * its own, so it leaves this unset.
   */
  audience?: string;
}

export interface AppConfig {
  apiBaseUrl: string;
  oidc: OidcConfig;
}

declare global {
  interface Window {
    // Explicitly includes undefined: under exactOptionalPropertyTypes an
    // optional property is not the same as one that may hold undefined, and
    // tests need to clear and restore it.
    __KONTORA_CONFIG__?: Partial<AppConfig> | undefined;
  }
}

/**
 * Reads and validates the injected configuration.
 *
 * Missing values throw rather than defaulting. A silently wrong authority or API
 * URL surfaces as a confusing login loop or a wall of CORS errors; failing at
 * start-up with the missing key named is far cheaper to diagnose.
 */
function readConfig(): AppConfig {
  const raw = window.__KONTORA_CONFIG__;
  if (!raw) {
    throw new Error(
      "Kontora configuration is missing. /config.js did not load or did not set window.__KONTORA_CONFIG__.",
    );
  }

  const missing: string[] = [];
  const require = <T>(value: T | undefined, name: string): T => {
    if (value === undefined || value === null || value === "") {
      missing.push(name);
      return undefined as T;
    }
    return value;
  };

  const oidc = raw.oidc ?? ({} as Partial<OidcConfig>);
  const config: AppConfig = {
    apiBaseUrl: require(raw.apiBaseUrl, "apiBaseUrl"),
    oidc: {
      authority: require(oidc.authority, "oidc.authority"),
      clientId: require(oidc.clientId, "oidc.clientId"),
      redirectUri: require(oidc.redirectUri, "oidc.redirectUri"),
      postLogoutRedirectUri:
        oidc.postLogoutRedirectUri ?? window.location.origin + "/",
      scope: oidc.scope ?? "openid profile email",
      ...(oidc.audience ? { audience: oidc.audience } : {}),
    },
  };

  if (missing.length > 0) {
    throw new Error(`Kontora configuration is incomplete: ${missing.join(", ")}`);
  }
  return config;
}

let cached: AppConfig | undefined;

/** Returns the validated configuration, reading it once. */
export function getConfig(): AppConfig {
  cached ??= readConfig();
  return cached;
}

/** Resets the cache. Tests only. */
export function resetConfigForTests(): void {
  cached = undefined;
}
